/**
 * Per-key tenant IP allowlist (#1708): shared validation and matching.
 *
 * Platform validates entries on `/v1/keys` writes; the gateway matches the
 * request IP against the published list on every generation request. Both
 * sides import THIS module so an entry the platform accepted is always an
 * entry the gateway can match — a divergence here would let a tenant store a
 * list that silently never fires.
 *
 * No `node:net`: contracts is a strip-types source workspace consumed by
 * Workers-compatible runtimes, so parsing is hand-rolled. Addresses are
 * normalized to BigInt for prefix comparison; IPv4-mapped IPv6
 * (`::ffff:a.b.c.d`) is folded onto IPv4 so a v4 entry matches the same
 * client however the proxy spelled its address.
 */

import { type ContractValidationResult, invalid, valid } from './validation.ts';

/** Hard cap on entries per key; enforced at validation, assumed by matching. */
export const IP_ALLOWLIST_MAX_ENTRIES = 100;

/** Longest accepted entry (`<ip>/<prefix>`); anything longer is malformed. */
export const IP_ALLOWLIST_MAX_ENTRY_LENGTH = 64;

interface ParsedIp {
  readonly family: 4 | 6;
  readonly value: bigint;
}

interface ParsedEntry {
  readonly ip: ParsedIp;
  readonly prefix: number;
  /** Canonical text form (lowercased v6, no leading zeros, explicit CIDR kept). */
  readonly normalized: string;
}

function parseIpv4(text: string): bigint | null {
  const parts = text.split('.');
  if (parts.length !== 4) return null;
  let value = 0n;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    // Reject leading zeros ("01") — ambiguous octal spellings have no place
    // in a security list.
    if (part.length > 1 && part.startsWith('0')) return null;
    const octet = Number(part);
    if (octet > 255) return null;
    value = (value << 8n) | BigInt(octet);
  }
  return value;
}

function parseIpv6(text: string): bigint | null {
  const lower = text.toLowerCase();
  if (lower.includes(':::')) return null;
  const doubleColonSplit = lower.split('::');
  if (doubleColonSplit.length > 2) return null;

  const parseGroups = (segment: string): bigint[] | null => {
    if (segment === '') return [];
    const groups: bigint[] = [];
    for (const group of segment.split(':')) {
      if (/^[0-9a-f]{1,4}$/.test(group)) {
        groups.push(BigInt(`0x${group}`));
        continue;
      }
      // Trailing dotted-quad form (`::ffff:1.2.3.4`): only valid as the last
      // group pair.
      const v4 = parseIpv4(group);
      if (v4 === null || group !== segment.split(':').at(-1)) return null;
      groups.push((v4 >> 16n) & 0xffffn, v4 & 0xffffn);
    }
    return groups;
  };

  const head = parseGroups(doubleColonSplit[0] ?? '');
  if (head === null) return null;
  let groups: bigint[];
  if (doubleColonSplit.length === 2) {
    const tail = parseGroups(doubleColonSplit[1] ?? '');
    if (tail === null) return null;
    const missing = 8 - head.length - tail.length;
    if (missing < 1) return null;
    groups = [...head, ...Array.from({ length: missing }, () => 0n), ...tail];
  } else {
    groups = head;
  }
  if (groups.length !== 8) return null;
  let value = 0n;
  for (const group of groups) {
    value = (value << 16n) | group;
  }
  return value;
}

/** Parse one address; v4-mapped v6 (`::ffff:a.b.c.d`) folds to family 4. */
export function parseIpAddress(raw: string): ParsedIp | null {
  const text = raw.trim();
  if (text.length === 0 || text.length > IP_ALLOWLIST_MAX_ENTRY_LENGTH) return null;
  if (text.includes(':')) {
    const value = parseIpv6(text);
    if (value === null) return null;
    if (value >> 32n === 0xffffn) {
      return { family: 4, value: value & 0xffffffffn };
    }
    return { family: 6, value };
  }
  const value = parseIpv4(text);
  if (value === null) return null;
  return { family: 4, value };
}

function formatIpv4(value: bigint): string {
  return [value >> 24n, (value >> 16n) & 0xffn, (value >> 8n) & 0xffn, value & 0xffn].join('.');
}

function formatIpv6(value: bigint): string {
  const groups: string[] = [];
  for (let shift = 112n; shift >= 0n; shift -= 16n) {
    groups.push(((value >> shift) & 0xffffn).toString(16));
  }
  // RFC 5952 zero compression is cosmetic; plain grouped form is already
  // canonical for equality/dedup because every group is minimal lowercase hex.
  return groups.join(':');
}

/**
 * Parse one allowlist entry — an exact IPv4/IPv6 address or CIDR. Returns the
 * parsed form plus a normalized spelling, or null when malformed.
 */
export function parseIpAllowlistEntry(raw: string): ParsedEntry | null {
  const text = raw.trim();
  if (text.length === 0 || text.length > IP_ALLOWLIST_MAX_ENTRY_LENGTH) return null;
  const slash = text.indexOf('/');
  const addressText = slash === -1 ? text : text.slice(0, slash);
  const ip = parseIpAddress(addressText);
  if (ip === null) return null;
  const maxPrefix = ip.family === 4 ? 32 : 128;
  let prefix = maxPrefix;
  if (slash !== -1) {
    const prefixText = text.slice(slash + 1);
    if (!/^\d{1,3}$/.test(prefixText)) return null;
    if (prefixText.length > 1 && prefixText.startsWith('0')) return null;
    prefix = Number(prefixText);
    if (prefix > maxPrefix) return null;
  }
  const formatted = ip.family === 4 ? formatIpv4(ip.value) : formatIpv6(ip.value);
  return {
    ip,
    prefix,
    normalized: slash === -1 && prefix === maxPrefix ? formatted : `${formatted}/${prefix}`,
  };
}

/**
 * Validate a tenant-supplied allowlist: array of parseable entries, deduped
 * after normalization, at most {@link IP_ALLOWLIST_MAX_ENTRIES}. Returns the
 * normalized entries. An empty array is valid and means "unrestricted" — the
 * write path stores it as NULL so an empty list and a cleared list are the
 * same configuration.
 */
export function validateIpAllowlist(raw: unknown): ContractValidationResult<string[]> {
  if (!Array.isArray(raw)) {
    return invalid('ip_allowlist must be an array of IP or CIDR strings');
  }
  if (raw.length > IP_ALLOWLIST_MAX_ENTRIES) {
    return invalid(`ip_allowlist must contain at most ${IP_ALLOWLIST_MAX_ENTRIES} entries`);
  }
  const normalized: string[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (typeof entry !== 'string') {
      return invalid('ip_allowlist entries must be strings');
    }
    const parsed = parseIpAllowlistEntry(entry);
    if (parsed === null) {
      return invalid(`ip_allowlist entry is not a valid IP or CIDR: ${entry.slice(0, IP_ALLOWLIST_MAX_ENTRY_LENGTH)}`);
    }
    if (seen.has(parsed.normalized)) continue;
    seen.add(parsed.normalized);
    normalized.push(parsed.normalized);
  }
  return valid(normalized);
}

/**
 * Parse the stored `ip_allowlist_json` column value (TEXT, written by the
 * platform as a JSON array of normalized entries). Tolerant by design: NULL,
 * absent, `[]`, malformed JSON, or a non-array all read as `null` =
 * unrestricted — a garbled stored value must degrade to "no restriction"
 * rather than lock a paying tenant out, and the write path is the place that
 * guarantees well-formedness.
 */
export function parseStoredIpAllowlist(raw: unknown): string[] | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const entries = parsed.filter((entry): entry is string => typeof entry === 'string');
  return entries.length === 0 ? null : entries;
}

function matchesEntry(ip: ParsedIp, entry: ParsedEntry): boolean {
  if (ip.family !== entry.ip.family) return false;
  const width = BigInt(ip.family === 4 ? 32 : 128);
  const shift = width - BigInt(entry.prefix);
  return ip.value >> shift === entry.ip.value >> shift;
}

/**
 * True when `clientIp` matches at least one entry. Unparseable client IPs and
 * malformed stored entries never match: the gate treats "no match" on an armed
 * list as a rejection, so a garbled input fails closed rather than open.
 */
export function ipMatchesAllowlist(clientIp: string, entries: readonly string[]): boolean {
  const ip = parseIpAddress(clientIp);
  if (ip === null) return false;
  for (const entryText of entries) {
    const entry = parseIpAllowlistEntry(entryText);
    if (entry !== null && matchesEntry(ip, entry)) return true;
  }
  return false;
}
